import { useEffect, useState } from "react";
import { ShieldCheck, Lock, Loader2, PackageCheck, Wallet } from "lucide-react";
import { fetchEscrowEligibility, startEscrow } from "@/lib/api";
import { fmtPrice } from "@/lib/utils";
import { useAuth } from "@/lib/AuthContext";
import { PaymentMethodPicker } from "@/components/PaymentMethodPicker";

export const EscrowBuyPanel = ({ annonce }) => {
  const { user, openAuthModal } = useAuth();
  const [elig, setElig] = useState(null);
  const [open, setOpen] = useState(false);
  const [phone, setPhone] = useState("");
  const [payOpts, setPayOpts] = useState({ payment_method: "mobile_money", customer_country: "FR" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => { if (annonce?.id) fetchEscrowEligibility(annonce.id).then(setElig).catch(() => setElig(null)); }, [annonce?.id]);
  useEffect(() => { setPhone(user?.phone_e164 || user?.phone || ""); }, [user]);

  const isOwner = !!user && user.user_id === annonce.owner_user_id;
  if (!elig?.eligible || isOwner) return null;

  const pay = async () => {
    if (!user) { openAuthModal(); return; }
    setBusy(true); setErr("");
    try {
      const r = await startEscrow({ annonce_id: annonce.id, phone, ...payOpts });
      window.location.href = r.payment_url;
    } catch (e) {
      const d = e?.response?.data?.detail;
      setErr(typeof d === "string" ? d : d?.[0]?.msg || "Erreur de paiement");
      setBusy(false);
    }
  };

  return (
    <div className="mt-4 rounded-2xl border-2 border-emerald-300 bg-emerald-50/60 p-4" data-testid="escrow-panel">
      <button type="button" onClick={() => setOpen((v) => !v)} className="w-full inline-flex items-center justify-between gap-2 text-left" data-testid="escrow-toggle-btn">
        <span className="inline-flex items-center gap-2 font-bold text-emerald-800"><ShieldCheck size={18} /> Acheter en paiement securise</span>
        <span className="text-xs font-semibold text-emerald-700 bg-white px-2 py-1 rounded-full">Protection acheteur</span>
      </button>
      <p className="text-xs text-emerald-900/80 mt-2">Vous payez Fodindin, pas le vendeur. Il n&apos;est paye qu&apos;apres votre confirmation de reception. Litige possible sous {elig.auto_release_hours} h.</p>
      {open && (
        <div className="mt-4 space-y-3" data-testid="escrow-form">
          <ol className="grid grid-cols-3 gap-2 text-[11px] text-emerald-900">
            <li className="rounded-xl bg-white p-2 text-center"><Lock size={14} className="mx-auto mb-1" /> 1. Vous payez, fonds bloques</li>
            <li className="rounded-xl bg-white p-2 text-center"><PackageCheck size={14} className="mx-auto mb-1" /> 2. Le vendeur livre</li>
            <li className="rounded-xl bg-white p-2 text-center"><Wallet size={14} className="mx-auto mb-1" /> 3. Vous confirmez, il est paye</li>
          </ol>
          <div className="flex items-baseline justify-between text-sm">
            <span>Montant securise</span><b data-testid="escrow-amount">{fmtPrice(elig.amount)}</b>
          </div>
          <div className="text-[11px] text-emerald-900/70">Frais de protection {Math.round(elig.commission_rate * 100)} % ({fmtPrice(elig.commission)}) a la charge du vendeur, deduits a son versement.</div>
          <PaymentMethodPicker value={payOpts} onChange={setPayOpts} amount={elig.amount} />
          <label className="block">
            <span className="text-xs font-semibold text-[var(--ink-soft)] uppercase tracking-wider">Votre numero (SMS de suivi)</span>
            <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+225 07 00 00 00 00" data-testid="escrow-phone-input" />
          </label>
          <button type="button" onClick={pay} disabled={busy || phone.replace(/\D/g, "").length < 8} className="btn-green w-full inline-flex items-center justify-center gap-2" data-testid="escrow-pay-btn">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <ShieldCheck size={16} />} {user ? `Payer ${fmtPrice(elig.amount)} en securite` : "Connectez-vous pour payer en securite"}
          </button>
          {err && <div className="text-sm font-semibold text-red-600" data-testid="escrow-error">{err}</div>}
        </div>
      )}
    </div>
  );
};
